<script setup lang="ts">
import { computed, ref } from 'vue';
import { buttonProps } from 'naive-ui';
import { gaiaFontWeight } from '../themes/tokens';

const props = defineProps({
  ...buttonProps,
  /** Await onClick (sync or async) and keep the button disabled until it settles. */
  spamProof: {
    type: Boolean,
    default: true,
  },
});

const pending = ref(false);

// Text-button weights live on our own classes (a per-variant weight has no
// naive var) and one of them is a :hover, so they arrive via v-bind.
const semibold = String(gaiaFontWeight.semibold);
const bold = String(gaiaFontWeight.bold);

// onClick is invoked by our wrapper, spamProof is ours — keep both off n-button.
const passThrough = computed(() => {
  const { onClick, spamProof, ...rest } = props;
  return rest;
});

// naive types onClick as `=> void`, but an async handler is assignable to that
// and really returns a promise — the promise spam-proofing waits on.
type ClickHandler = (e: MouseEvent) => void | Promise<void>;

async function handleClick(e: MouseEvent): Promise<void> {
  if (!props.onClick) return;
  const handlers: ClickHandler[] = Array.isArray(props.onClick) ? props.onClick : [props.onClick];
  if (!props.spamProof) {
    for (const handler of handlers) await handler(e);
    return;
  }
  if (pending.value) return;
  pending.value = true;
  try {
    await Promise.all(handlers.map(handler => handler(e)));
  } finally {
    pending.value = false;
  }
}
</script>

<template>
  <n-button
    v-bind="passThrough"
    :disabled="props.disabled || pending"
    :class="{ 'gaia-button--text': !!props.text }"
    @click="handleClick"
  >
    <template v-if="$slots.icon" #icon>
      <slot name="icon" />
    </template>
    <!-- A hidden bold copy of the label reserves the bold width up front, so
         the hover weight change doesn't reflow the text. -->
    <n-flex v-if="props.text" inline vertical align="center" :size="0">
      <slot />
      <span class="gaia-button__text-ghost" aria-hidden="true"><slot /></span>
    </n-flex>
    <template v-else>
      <slot />
    </template>
  </n-button>
</template>

<style scoped>
/* Text buttons ("View all"): a per-variant weight has no naive-ui theme var;
   the box reset keeps them sitting inline like text. No underline — hover
   affordance is the weight bump. */
.gaia-button--text {
  font-weight: v-bind(semibold);
  height: auto;
  padding: 0;
}

.gaia-button--text:hover {
  font-weight: v-bind(bold);
}

.gaia-button__text-ghost {
  font-weight: v-bind(bold);
  height: 0;
  overflow: hidden;
  visibility: hidden;
}
</style>
